import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, FileText, Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getDefaultProvider } from "@/ai/providers";
import { getRecommendedModel } from "@/ai/models";
import { marked } from "marked";
import { generateAIText } from "@/lib/ai-text";

export default function UploadPolicy() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [category, setCategory] = useState("");
  const [version, setVersion] = useState("1");
  const [policyTitle, setPolicyTitle] = useState("");
  const [parsedContent, setParsedContent] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const formatTitleFromFilename = (fileName: string) => {
    const withoutExtension = fileName.replace(/\.[^/.]+$/, "");
    return withoutExtension
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const fileToDataUrl = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    const validExtensions = ["pdf", "txt", "doc", "docx"];

    if (!validExtensions.includes(extension)) {
      toast({
        title: "Unsupported file type",
        description: "Please upload a PDF, DOC, DOCX, or TXT file.",
        variant: "destructive",
      });
      event.target.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload a file under 10MB.",
        variant: "destructive",
      });
      event.target.value = "";
      return;
    }

    setSelectedFile(file);
    setParsedContent("");
    setPolicyTitle(formatTitleFromFilename(file.name));
  };

  const handleParseDocument = async () => {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select a policy document first.",
        variant: "destructive",
      });
      return;
    }

    setIsParsing(true);
    try {
      const provider = getDefaultProvider();
      const model = getRecommendedModel(provider, "PARSING");
      const dataUrl = await fileToDataUrl(selectedFile);

      const systemPrompt =
        "You are a document parser for corporate policy ingestion. Extract the complete readable text from the file and output clean Markdown. Preserve headings, section structure, numbered lists, and bullet points. Remove page numbers, repeated headers/footers, and formatting artifacts.";

      const extracted = await generateAIText({
        provider,
        model,
        system: systemPrompt,
        messages: [
          {
            role: "user",
            content: [
              {
                type: "file",
                data: dataUrl,
                mediaType: selectedFile.type || "application/octet-stream",
                filename: selectedFile.name,
              },
            ],
          },
        ],
        temperature: 0,
      });
      const extractedText = extracted?.trim() || "";
      if (!extractedText) {
        throw new Error("No text could be extracted from this document.");
      }

      const titleMatch = extractedText.match(/^#\s+(.+)$/m);
      if (titleMatch?.[1]?.trim()) {
        setPolicyTitle(titleMatch[1].trim());
      }

      setParsedContent(extractedText);
      toast({
        title: "Parsing complete",
        description: "Review the extracted content, then save as a draft policy.",
      });
    } catch (error: any) {
      console.error("Document parse error:", error);
      toast({
        title: "Parsing failed",
        description: error?.message || "Unable to parse this policy document.",
        variant: "destructive",
      });
    } finally {
      setIsParsing(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!profile?.id || !profile.company_id) {
      toast({
        title: "Unable to save",
        description: "Your account is missing company details.",
        variant: "destructive",
      });
      return;
    }

    if (!policyTitle.trim() || !parsedContent.trim()) {
      toast({
        title: "Missing content",
        description: "Parse a policy and provide a title before saving.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      marked.setOptions({ gfm: true, breaks: true });
      const htmlContent = marked.parse(parsedContent);

      const { error } = await supabase.from("policies").insert({
        title: policyTitle.trim(),
        content: typeof htmlContent === "string" ? htmlContent : parsedContent,
        department: category.trim() || "general",
        version: Number(version) > 0 ? Number(version) : 1,
        status: "draft",
        company_id: profile.company_id,
        created_by: profile.id,
        description: selectedFile
          ? `Imported and parsed from ${selectedFile.name}`
          : "Imported via Upload Policy",
      });

      if (error) throw error;

      toast({
        title: "Draft policy created",
        description: "Your uploaded policy has been saved as a draft.",
      });
      navigate("/policy-library", { state: { refresh: true } });
    } catch (error: any) {
      console.error("Failed to save policy draft:", error);
      toast({
        title: "Save failed",
        description: error?.message || "Failed to save policy draft.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleBulkUpload = () => {
    toast({
      title: "Bulk Upload Initiated",
      description: "Multiple policies are being processed. You'll be notified when complete.",
    });
    navigate("/policy-library");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="h-9 w-9"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Upload Policy</h1>
          <p className="text-muted-foreground">
            Upload policy documents to your library
          </p>
        </div>
      </div>

      {/* Content */}
      <Tabs defaultValue="single" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="single">Single Upload</TabsTrigger>
          <TabsTrigger value="bulk">Bulk Import</TabsTrigger>
        </TabsList>
        
        <TabsContent value="single" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Upload Single Policy
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="policy-file">Policy Document</Label>
                <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                  <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Drag and drop your policy file here</p>
                    <p className="text-xs text-muted-foreground">Supports PDF, DOCX, TXT files up to 10MB</p>
                    {selectedFile && (
                      <p className="text-xs text-foreground font-medium">{selectedFile.name}</p>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Browse Files
                    </Button>
                    <input
                      ref={fileInputRef}
                      id="policy-file"
                      type="file"
                      accept=".pdf,.doc,.docx,.txt,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Input
                    placeholder="e.g., Security, HR, Finance"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Version</Label>
                  <Input
                    placeholder="e.g., 1"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                  />
                </div>
              </div>

              <Button onClick={handleParseDocument} className="w-full" disabled={isParsing || !selectedFile}>
                <Upload className="w-4 h-4 mr-2" />
                {isParsing ? "Parsing..." : "Parse with AI"}
              </Button>

              {parsedContent && (
                <div className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label>Policy Title</Label>
                    <Input
                      value={policyTitle}
                      onChange={(e) => setPolicyTitle(e.target.value)}
                      placeholder="Policy title"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Parsed Preview</Label>
                    <Textarea
                      value={parsedContent}
                      onChange={(e) => setParsedContent(e.target.value)}
                      className="min-h-[300px]"
                    />
                  </div>

                  <Button
                    onClick={handleSaveDraft}
                    className="w-full"
                    disabled={isSaving || !policyTitle.trim() || !parsedContent.trim()}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {isSaving ? "Saving Draft..." : "Save as Draft"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="bulk" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Bulk Import Policies
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted p-4 rounded-lg">
                <h4 className="font-medium mb-2">Bulk Import Requirements:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• ZIP file containing multiple policy documents</li>
                  <li>• Include metadata.csv file for policy information</li>
                  <li>• Maximum 50 policies per upload</li>
                  <li>• Each file must be under 10MB</li>
                </ul>
              </div>
              
              <div className="space-y-4">
                <Button variant="outline" className="w-full">
                  <Download className="w-4 h-4 mr-2" />
                  Download Template CSV
                </Button>
                
                <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                  <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Upload your ZIP file here</p>
                    <p className="text-xs text-muted-foreground">ZIP files up to 100MB supported</p>
                    <Button variant="outline" size="sm">
                      Browse Files
                    </Button>
                  </div>
                </div>
              </div>
              
              <Button onClick={handleBulkUpload} className="w-full">
                <Upload className="w-4 h-4 mr-2" />
                Start Bulk Import
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

